import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Volume2, Pause, Play, Square, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** ListenButton - reads lesson text aloud with the browser's built-in TTS.
 *  Free, offline-capable, works on mobile. Prefers an en-AU voice. */
export const ListenButton = ({ text, label = "Listen" }) => {
  const [state, setState] = useState("idle"); // idle | playing | paused
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const utterRef = useRef(null);

  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);

  if (!supported || !text) return null;

  const pickVoice = () => {
    const voices = window.speechSynthesis.getVoices() || [];
    return (
      voices.find((v) => v.lang === "en-AU") ||
      voices.find((v) => v.lang?.startsWith("en-GB")) ||
      voices.find((v) => v.lang?.startsWith("en")) ||
      null
    );
  };

  const start = () => {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = pickVoice();
    if (voice) u.voice = voice;
    u.rate = 0.95;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setState("playing");
  };

  const toggle = () => {
    if (state === "idle") return start();
    if (state === "playing") {
      window.speechSynthesis.pause();
      setState("paused");
    } else {
      window.speechSynthesis.resume();
      setState("playing");
    }
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setState("idle");
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        onClick={toggle}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        data-testid="listen-toggle-btn"
        aria-label={state === "playing" ? "Pause reading" : "Read lesson aloud"}
      >
        {state === "idle" && <Volume2 className="w-4 h-4" />}
        {state === "playing" && <Pause className="w-4 h-4" />}
        {state === "paused" && <Play className="w-4 h-4" />}
        {state === "idle" ? label : state === "playing" ? "Pause" : "Resume"}
      </button>
      {state !== "idle" && (
        <button
          onClick={stop}
          className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
          data-testid="listen-stop-btn"
          aria-label="Stop reading"
        >
          <Square className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

/** PremiumListenButton - Pro perk: natural ElevenLabs narration, chunked & queued.
 *  Renders nothing for free users. */
const chunkText = (text, maxLen = 2400) => {
  const sentences = text.split(/(?<=[.!?])\s+/);
  const chunks = [];
  let cur = "";
  for (const s of sentences) {
    if ((cur + " " + s).length > maxLen && cur) {
      chunks.push(cur.trim());
      cur = s;
    } else {
      cur = cur ? `${cur} ${s}` : s;
    }
  }
  if (cur.trim()) chunks.push(cur.trim());
  return chunks;
};

export const PremiumListenButton = ({ text }) => {
  const { isPro } = useAuth();
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const [part, setPart] = useState(0);
  const chunksRef = useRef([]);
  const cacheRef = useRef({});
  const audioRef = useRef(null);
  const stoppedRef = useRef(false);

  useEffect(() => () => {
    stoppedRef.current = true;
    if (audioRef.current) audioRef.current.pause();
    if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  if (!isPro || !text) return null;

  // Premium (ElevenLabs) failed — never surface a raw server/Cloudflare error.
  // Show one friendly message and fall back to the free browser voice.
  const failToFreeVoice = () => {
    setState("idle");
    setPart(0);
    toast.message("Premium voice isn't available right now, try Listen to lesson", {
      description: "Playing with the free voice instead.",
    });
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        const voices = window.speechSynthesis.getVoices() || [];
        const v = voices.find((x) => x.lang === "en-AU")
          || voices.find((x) => x.lang?.startsWith("en-GB"))
          || voices.find((x) => x.lang?.startsWith("en"));
        if (v) u.voice = v;
        u.rate = 0.95;
        window.speechSynthesis.speak(u);
      }
    } catch { /* ignore */ }
  };

  const fetchChunk = async (i) => {
    if (cacheRef.current[i]) return cacheRef.current[i];
    const r = await axios.post(`${API}/tutor/lesson-audio`, { text: chunksRef.current[i] }, { withCredentials: true });
    cacheRef.current[i] = r.data.audio_url;
    return r.data.audio_url;
  };

  const playIdx = async (i) => {
    if (stoppedRef.current) return;
    if (i >= chunksRef.current.length) { setState("idle"); setPart(0); return; }
    setPart(i + 1);
    try {
      const url = await fetchChunk(i);
      if (stoppedRef.current) return;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => playIdx(i + 1);
      audio.onerror = () => { if (!stoppedRef.current) failToFreeVoice(); };
      await audio.play();
      setState("playing");
      // Pre-generate (and cache) the next section one at a time.
      if (i + 1 < chunksRef.current.length) fetchChunk(i + 1).catch(() => {});
    } catch (err) {
      if (!stoppedRef.current) failToFreeVoice();
    }
  };

  const start = async () => {
    stoppedRef.current = false;
    chunksRef.current = chunkText(text);
    setState("loading");
    await playIdx(0);
  };

  const toggle = () => {
    if (state === "idle") return start();
    if (state === "playing") { audioRef.current?.pause(); setState("paused"); }
    else if (state === "paused") { audioRef.current?.play(); setState("playing"); }
  };

  const stop = () => {
    stoppedRef.current = true;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setState("idle");
    setPart(0);
  };

  const totalParts = chunksRef.current.length;

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        onClick={toggle}
        disabled={state === "loading"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-warning/40 bg-warning/10 text-sm text-warning hover:bg-warning/20 transition-colors"
        data-testid="premium-listen-btn"
        aria-label="Listen with premium voice"
      >
        {state === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
        {state === "idle" && <Sparkles className="w-4 h-4" />}
        {state === "playing" && <Pause className="w-4 h-4" />}
        {state === "paused" && <Play className="w-4 h-4" />}
        {state === "idle" ? "Premium voice" : state === "loading" ? "Preparing…" : state === "playing" ? `Pause${totalParts > 1 ? ` (${part}/${totalParts})` : ""}` : "Resume"}
      </button>
      {(state === "playing" || state === "paused") && (
        <button
          onClick={stop}
          className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
          data-testid="premium-listen-stop-btn"
          aria-label="Stop premium audio"
        >
          <Square className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};
